import { PropsWithChildren } from 'react';
import toast from 'react-hot-toast';
import { rgb } from '@design-system/tokens';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faPenToSquare } from '@fortawesome/pro-light-svg-icons';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { copyToClipboard } from '@client/new/components/copy-to-clipboard';

import { Col, Row } from '../grid/grid';
import { Tooltip } from '../tooltip/tooltip';
import { Typography } from '../typography/typography';
import { Modal, ModalProps } from '../modal/modal';
import { Button } from '../button/button';

import * as styles from './CopyLinkModal.css';

type CopyCardProps = {
  url: string;
  linkTitle: string;
  tooltip?: React.ReactElement;
  onEdit?: () => void;
};
export const CopyCard = (p: PropsWithChildren<CopyCardProps>) => (
  <Row className={styles.wrapper} gap="l">
    <Col>
      <Row gap="m" alignCenter>
        <a
          href={p.url}
          target="_blank"
          rel="noreferrer"
          className={styles.link}
        >
          <Typography variant="body" size="large" fontWeight="medium">
            {p.linkTitle}
          </Typography>
        </a>
        {p.tooltip ? (
          <Tooltip title={p.tooltip} variant="white">
            <FontAwesomeIcon color={rgb('P-80')} icon={faQuestionCircle} />
          </Tooltip>
        ) : null}
      </Row>
      <Row className={styles.text}>{p.children}</Row>
    </Col>
    <Row alignCenter justifyCenter gap="m">
      <Row
        alignCenter
        justifyCenter
        onClick={() => {
          copyToClipboard(p.url);
          toast.success(`${p.linkTitle} copied to clipboard`);
        }}
        className={styles.copyButton}
      >
        <FontAwesomeIcon color={rgb('B-100')} icon={faCopy} size={'lg'} />
      </Row>
      {p.onEdit ? (
        <Row
          alignCenter
          justifyCenter
          onClick={p.onEdit}
          className={styles.copyButton}
        >
          <FontAwesomeIcon
            color={rgb('B-100')}
            icon={faPenToSquare}
            size={'lg'}
          />
        </Row>
      ) : null}
    </Row>
  </Row>
);

export type CopyLinkModalProps = Pick<
  ModalProps,
  'isOpen' | 'onClose' | 'title'
> &
  CopyCardProps & { linkDesc?: string; desc?: string };

export const CopyLinkModal = (p: CopyLinkModalProps) => (
  <Modal isOpen={p.isOpen} onClose={p.onClose} title="" hideCloseButton>
    <Col gap="2xl">
      <Row justifyCenter>
        <Typography variant="display" size="small">
          {p.title}
        </Typography>
      </Row>
      <CopyCard url={p.url} linkTitle={p.linkTitle} tooltip={p.tooltip}>
        <Typography variant="body" size="medium" fontWeight="normal">
          {p.linkDesc}
        </Typography>
      </CopyCard>
      {p.desc ? (
        <Row className={styles.desc}>
          <Typography variant="body" size="medium" fontWeight="normal">
            {p.desc}
          </Typography>
        </Row>
      ) : null}

      <Row justifyCenter>
        <Button
          onClick={() => {
            copyToClipboard(p.url);
            toast.success(`${p.linkTitle} copied to clipboard`);
            p.onClose();
          }}
          color="neutral"
          className={styles.closeButton}
        >
          {`Copy & Close`}
        </Button>
      </Row>
    </Col>
  </Modal>
);

export type CopyLinksModalProps = Pick<
  CopyLinkModalProps,
  'isOpen' | 'onClose' | 'title'
> & {
  links: Array<CopyCardProps & { linkDesc: string }>;
  beforeDesc?: React.ReactNode;
};

export const CopyLinksModal = (p: CopyLinksModalProps) => (
  <Modal isOpen={p.isOpen} onClose={p.onClose} title="" hideCloseButton>
    <Col gap="2xl">
      <Row>
        <Typography variant="display" size="small">
          {p.title}
        </Typography>
      </Row>
      {p.beforeDesc ? (
        <Row className={styles.desc}>
          <Typography variant="body" size="medium" fontWeight="normal">
            {p.beforeDesc}
          </Typography>
        </Row>
      ) : null}
      {p.links.map((l) => (
        <CopyCard
          url={l.url}
          linkTitle={l.linkTitle}
          tooltip={l.tooltip}
          key={l.url}
        >
          <Typography variant="body" size="medium" fontWeight="normal">
            {l.linkDesc}
          </Typography>
        </CopyCard>
      ))}
    </Col>
  </Modal>
);
